import type { RepairItemRef } from '@server/warehouse-repair';
import type { QueryKey } from '@tanstack/react-query';
import { Wrench } from 'lucide-react';
import { Component, lazy, Suspense, useState } from 'react';

import { Button } from '@/_shared/components/ui/button';

import type { RepairItemNoun } from './copy';

// The dialog loads on first open, so only this button sits in the initial bundle (ACT2-1008 budget).
const RepairDialog = lazy(() => import('./RepairDialog').then((m) => ({ default: m.RepairDialog })));

export const REPAIR_DIALOG_LOAD_FAILED = 'The Repair dialog could not load. Reload the page to continue.';

/**
 * Catches a failed load of the dialog's chunk — after a deploy, the old chunk is gone — so it stays
 * on this button instead of reaching the page's error boundary, which would replace the page and lose
 * unsaved work.
 */
class DialogLoadBoundary extends Component<{ children: React.ReactNode }, { failed: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <span role="alert" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        {REPAIR_DIALOG_LOAD_FAILED}
        <Button type="button" variant="secondary" size="sm" label="Reload" onClick={() => window.location.reload()} />
      </span>
    );
  }
}

/** A **Repair** button that opens the Repair dialog for one item (or one workflow step). */
export function RepairButton({
  itemRef,
  itemNoun,
  unassigned,
  invalidateKeys,
  subject,
  label = 'Repair',
  ariaLabel,
}: {
  itemRef: RepairItemRef;
  itemNoun: RepairItemNoun;
  unassigned: boolean;
  invalidateKeys: readonly QueryKey[];
  subject?: string;
  label?: string;
  ariaLabel?: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const trigger = (loading: boolean): React.ReactElement => (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      Icon={<Wrench aria-hidden />}
      label={label}
      aria-label={ariaLabel}
      loading={loading}
      onClick={() => setOpen(true)}
    />
  );
  return (
    <DialogLoadBoundary>
      {/* While the dialog's chunk loads, the button stands in busy and disabled. */}
      <Suspense fallback={trigger(true)}>
        {trigger(false)}
        {open && (
          <RepairDialog
            itemRef={itemRef}
            itemNoun={itemNoun}
            unassigned={unassigned}
            invalidateKeys={invalidateKeys}
            subject={subject}
            open={open}
            onOpenChange={setOpen}
          />
        )}
      </Suspense>
    </DialogLoadBoundary>
  );
}
